import { Button, Stack } from "@mantine/core"
import { useForm } from "@mantine/form"
import { settlementMethodOptions, tradeActionOptions } from "app/models/dropdown-options"
import { MasterTradeParams } from "app/models/master-trade-params"
import { editBookingDetails } from "app/state/ducks/master-trades/thunks"
import { getSubTrades } from "app/state/ducks/sub-trades/selectors"
import { formatDateToUTC, stringToDate } from "app/utils/util-functions"
import BlackDateInput from "app/views/components/inputs/BlackDateInput"
import BlackSelectInput from "app/views/components/inputs/BlackSelectInput"
import BlackTextInput from "app/views/components/inputs/BlackTextInput"
import { ErrorNotification, SuccessNotification } from "app/views/components/notifications/notification"
import WarningText from "app/views/components/warning-text/warning-text"
import { useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { showTradeId } from "../utils/master-trade-util"

type Params = {
    trade?: MasterTradeParams
    closeModal: () => void
    onHandleSubmit?: any
}

export default function EditBookingForm({
    trade,
    closeModal,
    onHandleSubmit
}: Params) {
    const dispatch = useDispatch()
    const [isLoading, setIsLoading] = useState(false)
    const subTrades = useSelector(getSubTrades)

    const form = useForm({
        initialValues: {
            clientId: trade?.clientId ?? '',
            settlementMethod: trade?.settlementMethod ?? '',
            outsideTradeId: trade?.outsideTradeId ?? '',
            tradeDate: trade?.tradeDate ? stringToDate(trade.tradeDate) : new Date(),
            tradeGroupId: trade?.tradeGroupId ?? '',
            tradeAction: trade?.myEntity ?? "Buy",
        },
        transformValues: (values) => ({
            ...values,
            tradeDate: formatDateToUTC(values.tradeDate),
        })
    })

    const isSettled = useMemo(() => {
        return subTrades.some(st => st.masterTradeId.id === trade?.id && st.status == "Settled")
    }, [subTrades])

    async function onSubmit(bookingDetails: any) {
        try {
            setIsLoading(true)
            const response: any = await dispatch(editBookingDetails(bookingDetails, trade))
            if (response.success) {
                SuccessNotification({
                    title: 'Successful Booking Details Saved',
                    message: 'You edited an booking details for open Trade',
                })
            } else {
                ErrorNotification({
                    title: 'Booking details save failed',
                    message: response.payload ?? 'Check data and try again',
                })
            }
            return undefined
        } catch {
            ErrorNotification({
                title: 'Booking Details save failed',
                message: 'Check data and try again',
            })
        } finally {
            setIsLoading(false)
        }
    }

    async function submitForm(event: any) {
        event.preventDefault()
        if (!form.validate().hasErrors) {
            const trade = await onSubmit(form.getTransformedValues())
            onHandleSubmit(trade ?? form.values)
        } else {
            ErrorNotification({
                title: 'Form Validation Error',
                message: 'Please fill in required fields',
            });
        }
    }

    return (
        <form onSubmit={submitForm} className="stepper-form">
            <Stack spacing="12px !important">
                <div className="edit-trade-input-field-tuple">
                    <BlackSelectInput label="Settlement Method" data={settlementMethodOptions} disabled={isSettled} {...form.getInputProps("settlementMethod")} />
                    {showTradeId(form.values.settlementMethod) ? (
                        <BlackTextInput
                            label={`${form.values.settlementMethod} Trade ID`}
                            {...form.getInputProps("outsideTradeId")}
                        />
                    ) : (
                        <div></div>
                    )}
                </div>
                <div className="edit-trade-input-field-tuple">
                    <BlackTextInput label="Trade ID" disabled value={trade?.systemId ?? ""} />
                    <BlackTextInput label="Trade Status" disabled value={trade?.status ?? ""} />
                </div>

                <div className="edit-trade-input-field-tuple">
                    <BlackTextInput label="Internal Trade Id" {...form.getInputProps("clientId")} />
                    <BlackDateInput label="Trade Date" disabled={isSettled} {...form.getInputProps("tradeDate")} />
                </div>

                <div className="edit-trade-input-field-tuple">
                    <BlackTextInput label="Trade Group ID" {...form.getInputProps("tradeGroupId")} />
                    <BlackSelectInput label="Trade Action" disabled={isSettled} data={tradeActionOptions} {...form.getInputProps("tradeAction")} />
                </div>
                {isSettled && <WarningText text="Trade Date, Trade Action, & Settlement Method can only be edited if there's no settled subtrades" />}
                <div className="action-buttons-block">
                    <Button className="cancel-button" onClick={() => closeModal()}>Cancel</Button>
                    <Button
                        type="submit"
                        loading={isLoading}
                        disabled={!form.isValid()}
                        className="delete-button stratch-delete-button"
                    >
                        Save
                    </Button>
                </div>
            </Stack>
        </form>
    )
}
